import { useCallback, useEffect, useRef, useState } from 'react';
import { Section, Box, Flex, Text, Button, Card, Dialog } from '@radix-ui/themes';
import { HeartIcon, HeartFilledIcon } from '@radix-ui/react-icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import PageTitle from 'components/layout/PageTitle';
import { userInfo } from 'store/slices/users';
import { fetchIssuesByForm, fetchVoteForms, toggleIssueLike } from 'util/formVote';

export default function Vote() {
  const currentUser = useSelector(userInfo);
  const currentUserId = currentUser?.userId;
  const requestSeq = useRef(0);

  const [loading, setLoading] = useState(true);
  const [formList, setFormList] = useState([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [issueList, setIssueList] = useState([]);
  const [issueLoading, setIssueLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');

  const fetchFormList = useCallback(async () => {
    setLoading(true);
    try {
      setFormList(await fetchVoteForms());
    } catch (error) {
      console.error('양식 목록 조회 실패:', error);
      setFormList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIssueList = useCallback(
    async (formId, { silent = false } = {}) => {
      if (!formId) return;
      const seq = ++requestSeq.current;
      if (!silent) {
        setIssueLoading(true);
        setIssueList([]);
      }

      try {
        const issues = await fetchIssuesByForm(formId, currentUserId);
        if (seq !== requestSeq.current) return;
        setIssueList(issues);
      } catch (error) {
        console.error('이슈 목록 조회 실패:', error);
        if (seq !== requestSeq.current) return;
        setIssueList([]);
      } finally {
        if (seq === requestSeq.current) setIssueLoading(false);
      }
    },
    [currentUserId]
  );

  const handleFormSelect = formId => {
    setSelectedForm(formId);
    fetchIssueList(formId);
  };

  const handleIssueClick = issueId => {
    window.open(`/localIssueFinder/detail/${issueId}`, '_blank');
  };

  // 투표 확인 dialog 열기
  const openConfirmDialog = issue => {
    setSelectedIssue(issue);
    setConfirmDialogOpen(true);
  };

  // 투표하기/취소하기 API 호출
  const handleLikeToggle = async () => {
    if (!selectedIssue) return;

    try {
      const response = await toggleIssueLike(selectedIssue.id, selectedIssue.isLiked);

      if (response.data.status === 200) {
        setAlertMessage(selectedIssue.isLiked ? '투표가 취소되었습니다.' : '투표가 완료되었습니다.');
        fetchIssueList(selectedForm, { silent: true });
      } else {
        setAlertMessage(response.data.message || '투표 처리 중 오류가 발생했습니다.');
        console.log('API 응답 오류:', response.data);
      }
    } catch (error) {
      console.error('투표 처리 실패:', error);
      setAlertMessage(error.response?.data?.message || '투표 처리 중 오류가 발생했습니다.');
    } finally {
      setConfirmDialogOpen(false);
      setAlertDialogOpen(true);
    }
  };

  useEffect(() => {
    fetchFormList();
  }, [fetchFormList]);

  return (
    <Section>
      <PageTitle title="투표 관리" />

      {loading ? (
        <Box p="6" style={{ textAlign: 'center' }}>
          <Text>로딩 중...</Text>
        </Box>
      ) : (
        <Box>
          <Box className="box box-white">
            <Text as="p" size="4" weight="bold" mb="var(--space-3)">
              양식을 선택하여주세요.
            </Text>

            {formList.length === 0 ? (
              <Text color="gray">활성화된 양식이 없습니다.</Text>
            ) : (
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, 320px)',
                  gap: '16px'
                }}
              >
                {formList.map(form => (
                  <Card
                    key={form.formId}
                    p="4"
                    style={{
                      border: selectedForm === form.formId ? '2px solid var(--accent-9)' : '1px solid var(--gray-6)',
                      backgroundColor: selectedForm === form.formId ? 'var(--accent-2)' : 'var(--color-background)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      height: '120px',
                      minHeight: '120px',
                      maxHeight: '120px'
                    }}
                    onClick={() => handleFormSelect(form.formId)}
                  >
                    <Flex direction="column" justify="between" style={{ height: '100%' }}>
                      <Flex align="flex-start" justify="between" gap="2">
                        <Text
                          size="4"
                          weight="medium"
                          style={{
                            flex: 1,
                            lineHeight: '1.4',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {form.formName}
                        </Text>
                        {selectedForm === form.formId && (
                          <Box
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: 'var(--accent-9)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <Text size="1" style={{ color: 'white' }}>
                              ✓
                            </Text>
                          </Box>
                        )}
                      </Flex>
                      {form.startDate && form.endDate && (
                        <Text size="2" color="gray">
                          {dayjs(form.startDate).format('YYYY-MM-DD')} ~ {dayjs(form.endDate).format('YYYY-MM-DD')}
                        </Text>
                      )}
                    </Flex>
                  </Card>
                ))}
              </Box>
            )}
          </Box>

          {selectedForm && (
            <Box className="box box-white">
              <Text as="p" size="4" weight="bold" mb="var(--space-3)">
                이슈 목록
              </Text>

              {issueLoading ? (
                <Box p="4" style={{ textAlign: 'center' }}>
                  <Text>이슈 목록 로딩 중...</Text>
                </Box>
              ) : issueList.length === 0 ? (
                <Text color="gray">해당 양식의 이슈가 없습니다.</Text>
              ) : (
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '20px'
                  }}
                >
                  {issueList.map((issue, index) => (
                    <Card
                      key={issue.id || index}
                      p="3"
                      style={{
                        border: '1px solid #e5e5e5',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Flex direction="column" gap="3">
                        <Box
                          className="thumbnail-container"
                          style={{
                            width: '100%',
                            aspectRatio: '16/9',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleIssueClick(issue.id)}
                        >
                          {issue.thumbnail ? (
                            <img
                              className="thumbnail-image"
                              src={issue.thumbnail}
                              alt={issue.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.3s ease-in-out'
                              }}
                            />
                          ) : (
                            <Flex align="center" justify="center" style={{ height: '100%' }}>
                              <Text size="2" color="gray">
                                이미지 없음
                              </Text>
                            </Flex>
                          )}
                        </Box>

                        <Flex direction="column" gap="2">
                          <Text
                            weight="medium"
                            style={{
                              fontSize: '20px',
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              cursor: 'pointer',
                              height: '2.8em',
                              minHeight: '2.8em',
                              maxHeight: '2.8em'
                            }}
                            onClick={() => handleIssueClick(issue.id)}
                          >
                            {issue.title}
                          </Text>

                          <Flex align="center" justify="between">
                            <Flex align="center" gap="1">
                              {(issue.thumbsUpCount || 0) > 0 ? (
                                <HeartFilledIcon size={20} color="var(--blue-9)" />
                              ) : (
                                <HeartIcon size={20} color="var(--blue-9)" />
                              )}
                              <Text color="blue" weight="medium" style={{ fontSize: '16px' }}>
                                {issue.thumbsUpCount || 0}
                              </Text>
                            </Flex>
                            <Button
                              variant={issue.isLiked ? 'solid' : 'outline'}
                              style={{ fontSize: '16px', padding: '8px 12px' }}
                              onClick={e => {
                                e.stopPropagation();
                                openConfirmDialog(issue);
                              }}
                            >
                              {issue.isLiked ? '투표취소' : '투표하기'}
                            </Button>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* 애니메이션을 위한 CSS */}
      <style>
        {`
          @keyframes slideInUp {
            0% {
              opacity: 0;
              transform: translateY(20px) scale(0.95);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes fadeInScale {
            0% {
              opacity: 0;
              transform: scale(0.95);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          /* 썸네일 호버 효과 - Image Zoom In Effect */
          .thumbnail-container:hover .thumbnail-image {
            transform: scale(1.2);
          }
        `}
      </style>

      {/* 투표 확인 Dialog */}
      <Dialog.Root open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <Dialog.Content style={{ maxWidth: 400 }}>
          <Dialog.Title>투표 확인</Dialog.Title>
          <Dialog.Description>{selectedIssue?.isLiked ? '투표를 취소하시겠습니까?' : '투표를 진행하시겠습니까?'}</Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Button variant="soft" onClick={() => setConfirmDialogOpen(false)}>
              취소
            </Button>
            <Button onClick={handleLikeToggle}>확인</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* 알림 Dialog */}
      <Dialog.Root open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <Dialog.Content style={{ maxWidth: 400 }}>
          <Dialog.Title>알림</Dialog.Title>
          <Dialog.Description style={{ whiteSpace: 'pre-line' }}>{alertMessage}</Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Button onClick={() => setAlertDialogOpen(false)}>확인</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Section>
  );
}
